import React, { useEffect, useState, useRef, useMemo } from "react";
import { CircleX, Search, Calendar as CalendarIcon, FileText, Download, User, Hash, DollarSign, Loader2, ArrowRight, Filter, ShieldCheck, Info, TrendingUp, Users, Zap } from "lucide-react";
import ReusableTable from "../components/Table/ReusableTable";
import Button from "../components/Button/Button";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";
import { post } from "../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";

const KpiCard = ({ title, value, icon: Icon, trend, color = "indigo" }) => (
  <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group animate-in fade-in slide-in-from-bottom-3 duration-500">
    <div className="flex justify-between items-start mb-4">
      <div className={`p-3 rounded-xl bg-${color}-50 text-${color}-500 group-hover:scale-110 transition-transform`}>
        <Icon size={20} />
      </div>
      {trend && (
        <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-full">
          <TrendingUp size={10} className="mr-0.5" /> {trend}
        </span>
      )}
    </div>
    <div>
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">{title}</p>
      <h3 className="text-xl font-bold text-gray-800 tracking-tight">{value}</h3>
    </div>
  </div>
);

const TransactionDetails = () => {
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");

  const fromCalendarRef = useRef(null);
  const toCalendarRef = useRef(null);

  const userDetails = localStorage.getItem("user_details")
    ? JSON.parse(localStorage.getItem("user_details"))
    : {};

  const userId = userDetails.id || null;
  const roleId = userDetails.role_id || null;
  const userPan = userDetails.pan_number || "";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromCalendarRef.current && !fromCalendarRef.current.contains(event.target)) setShowFromCalendar(false);
      if (toCalendarRef.current && !toCalendarRef.current.contains(event.target)) setShowToCalendar(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const exportToCSV = () => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(","),
      ...data.map((row) => headers.map((field) => `"${(row[field] ?? "").toString().replace(/"/g, '""')}"`).join(","))
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", "transaction_details.csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const payload = {
        distributor_pan_number: roleId === 2 ? userPan : "",
        from_date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "",
        to_date: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
        user_Id: userId,
        user_role_id: roleId,
      };
      const response = await post("/transaction/fetchTransactionsDetailsReport", payload);
      if (response.data?.Success) {
        const records = response.data.Data || [];
        const formatted = records.map((item) => ({
          transactionDetailsId: item.id,
          transactionId: item.transaction_id,
          transactionDate: moment(item.transaction_date).format("DD-MM-YYYY"),
          processMonth: item.process_month,
          clientName: item.client_name,
          pan: item.pan_number,
          folioNo: item.folio_no,
          distributorCode: item.distributor_arn_code,
          distributorPan: item.distributor_pan_number,
          productCode: item.product_code,
          capitalCommitment: item.capital_commitment,
          placementFeePercent: item.placement_fee_per,
          placementFeeAmount: item.placement_fee_amount,
          upfrontFeePercent: item.upfront_fee_per,
          upfrontFeeAmount: item.upfront_fee_amount,
          trailStartMonth: moment(item.trail_start_month).format("MM-YYYY"),
          trailEndMonth: moment(item.trail_end_month).format("MM-YYYY"),
          monthlyTrailAmount: item.monthly_trail_amount,
          totalCommission: item.total_commission,
          gst: item.gst,
          tds: item.tds,
          nextCommissionPayable: item.net_commission_payable,
          remarks: item.uploaded_remarks,
          uploadStatus: item.status_id,
          errorRemark: item.check_remarks,
          canMarkIsCorrect: item.CanMarkIsCorrect,
          FundName: item.fund_name,
          FundCode: item.fund_code,
          statusId: item.status_id,
          invoiceNumber: item.invoice_number,
          utrNumber: item.utr_number,
        }));
        setData(formatted);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const totalCommitment = data.reduce((sum, item) => sum + (Number(item.capitalCommitment) || 0), 0);
    const totalCommission = data.reduce((sum, item) => sum + (Number(item.totalCommission) || 0), 0);
    return {
      count: data.length,
      commitment: totalCommitment.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      commission: totalCommission.toLocaleString(undefined, { maximumFractionDigits: 0 }),
    };
  }, [data]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleMarkNotCorrect = async (transactionDetailsId, transactionId) => {
    try {
      const payload = { transaction_details_id: transactionDetailsId, transaction_id: transactionId, user_Id: userId };
      const response = await post("/transaction/notcorrectTransactionDetails", payload);
      if (response.data?.Success) {
        toast.success("Marked 'Not Correct' successfully.");
        fetchData();
      } else {
        toast.error("Failed to mark as 'Not Correct'.");
      }
    } catch (error) {
      toast.error("Error occurred while marking 'Not Correct'.");
    }
  };

  const columns = React.useMemo(() => [
    {
      Header: <div className="flex items-center gap-2"><CalendarIcon size={14} /> DATE</div>,
      accessor: "transactionDate",
      size: "160px"
    },
    { Header: "PROCESS MONTH", accessor: "processMonth", size: "140px" },
    {
      Header: <div className="flex items-center gap-2"><User size={14} /> CLIENT</div>,
      accessor: "clientName",
      size: "180px",
      Cell: ({ value }) => <span className="font-bold text-gray-900 uppercase tracking-tight break-words">{value || "N/A"}</span>
    },
    { Header: "PAN", accessor: "pan", size: "140px" },
    { Header: "FOLIO NO", accessor: "folioNo", size: "140px" },
    {
      Header: <div className="flex items-center gap-2"><FileText size={14} /> INVOICE</div>,
      accessor: "invoiceNumber",
      size: "140px",
      Cell: ({ value }) => value ? <span className="text-indigo-600 font-bold">{value}</span> : <span className="text-gray-300 italic">Pending</span>
    },
    { Header: "UTR NO", accessor: "utrNumber", size: "140px" },
    { Header: "APRN CODE", accessor: "distributorCode", size: "200px" },
    { Header: "FUND", accessor: "FundName", size: "180px" },
    {
      Header: <div className="flex items-center gap-2"> COMMITMENT</div>,
      accessor: "capitalCommitment",
      size: "180px",
      Cell: ({ value }) => <span className="font-black text-gray-800">{value ? Number(value).toLocaleString() : "0"}</span>
    },
    {
      Header: <div className="flex items-center gap-2"> TOTAL COMM</div>,
      accessor: "totalCommission",
      size: "160px",
      Cell: ({ value }) => <span className="text-emerald-600 font-black">{value ? Number(value).toLocaleString() : "0"}</span>
    },
    {
      Header: "STATUS",
      accessor: "uploadStatus",
      size: "160px",
      Cell: ({ value, row }) => {
        const { statusId, invoiceNumber } = row.original;
        if (statusId === 5) return <span className="text-[10px] font-bold uppercase bg-rose-50 text-rose-600 px-2 py-1 rounded-full border border-rose-100">Disputed</span>;
        if (invoiceNumber) return <span className="text-[10px] font-bold uppercase bg-emerald-50 text-emerald-600 px-2 py-1 rounded-full border border-emerald-100">Invoiced</span>;
        return <span className="text-[10px] font-bold uppercase bg-gray-50 text-gray-500 px-2 py-1 rounded-full border border-gray-100">Processed</span>;
      }
    },
    {
      Header: "ACTION",
      accessor: "actions",
      size: "160px",
      Cell: ({ row }) => {
        const { statusId, canMarkIsCorrect, transactionDetailsId, transactionId, invoiceNumber } = row.original;
        if (statusId === 5 && roleId === 3) return <span className="text-[10px] font-bold text-rose-500 italic">Reported Incorrect</span>;
        if (invoiceNumber) return null;
        if (roleId === 2 && canMarkIsCorrect) {
          return (
            <button
              onClick={() => handleMarkNotCorrect(transactionDetailsId, transactionId)}
              className="text-[11px] font-black uppercase text-rose-600 hover:text-rose-800 flex items-center gap-1 transition-all"
            >
              <CircleX size={14} /> Mark Incorrect
            </button>
          );
        }
        return null;
      },
    }
  ], [userId, roleId]);

  return (
    <div className="w-full max-w-screen-xl mx-auto p-6 h-full min-h-screen bg-gray-50/30 space-y-8 animate-in fade-in duration-700">
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar theme="colored" />

      {/* Premium Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-lg shadow-indigo-100 border border-indigo-500">
              <Zap size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800 tracking-tight leading-tight uppercase">Transaction Analytics</h1>
              <p className="text-[11px] font-medium text-gray-400 flex items-center gap-2 ml-1">
                Registry & Rewards <span className="w-1 h-1 rounded-full bg-gray-300" /> Comprehensive Movement Report
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-100 h-[46px] px-6 rounded-xl font-bold text-sm shadow-sm transition-all active:scale-95"
          >
            <Download size={18} className="text-indigo-600" />
            Export Data
          </button>
        </div>
      </div>

      {/* Modern Filter Actions Bar */}
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col xl:flex-row gap-6 xl:items-end">
        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* From Date */}
          <div className="flex flex-col gap-2" ref={fromCalendarRef}>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Analysis Period Start</label>
            <div className="relative group">
              <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-indigo-500 transition-colors" />
              <input
                type="text"
                readOnly
                value={fromDate ? dayjs(fromDate).format("DD MMM YYYY") : ""}
                placeholder="Select Start Date"
                onClick={() => { setShowFromCalendar(!showFromCalendar); setShowToCalendar(false); }}
                className="w-full pl-11 pr-11 h-[48px] border border-gray-100 rounded-xl text-sm font-bold text-gray-700 bg-gray-50/50 cursor-pointer hover:border-indigo-200 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
              />
              {fromDate && (
                <button onClick={(e) => { e.stopPropagation(); setFromDate(null); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-rose-500 transition-colors">
                  <CircleX size={16} />
                </button>
              )}
              {showFromCalendar && (
                <div className="absolute top-14 left-0 z-50 shadow-2xl rounded-2xl border border-gray-100 bg-white overflow-hidden animate-in fade-in slide-in-from-top-4 duration-500 p-2">
                  <Calendar
                    onChange={(date) => { setFromDate(date); setShowFromCalendar(false); }}
                    value={fromDate}
                    maxDate={toDate || new Date()}
                  />
                </div>
              )}
            </div>
          </div>

          {/* To Date */}
          <div className="flex flex-col gap-2" ref={toCalendarRef}>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Analysis Period End</label>
            <div className="relative group">
              <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-indigo-500 transition-colors" />
              <input
                type="text"
                readOnly
                value={toDate ? dayjs(toDate).format("DD MMM YYYY") : ""}
                placeholder="Select End Date"
                onClick={() => { setShowToCalendar(!showToCalendar); setShowFromCalendar(false); }}
                className="w-full pl-11 pr-11 h-[48px] border border-gray-100 rounded-xl text-sm font-bold text-gray-700 bg-gray-50/50 cursor-pointer hover:border-indigo-200 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
              />
              {toDate && (
                <button onClick={(e) => { e.stopPropagation(); setToDate(null); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-rose-500 transition-colors">
                  <CircleX size={16} />
                </button>
              )}
              {showToCalendar && (
                <div className="absolute top-14 left-0 z-50 shadow-2xl rounded-2xl border border-gray-100 bg-white overflow-hidden animate-in fade-in slide-in-from-top-4 duration-500 p-2">
                  <Calendar
                    onChange={(date) => { setToDate(date); setShowToCalendar(false); }}
                    value={toDate}
                    minDate={fromDate}
                    maxDate={new Date()}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="flex flex-col gap-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Quick Search</label>
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-indigo-500 transition-colors" />
              <input
                type="text"
                placeholder="Search Client, Folio, PAN..."
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                className="w-full pl-11 pr-4 h-[48px] border border-gray-100 rounded-xl text-sm font-bold text-gray-700 bg-gray-50/50 hover:border-indigo-200 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Report Table Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-bottom-8 duration-1000">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
            <span className="font-black uppercase tracking-widest text-[10px] text-gray-500">Registry Records: {data.length}</span>
          </div>
          {globalSearch && (
            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-50 px-4 py-1.5 rounded-full border border-indigo-100 animate-pulse">
              Filtered Results for "{globalSearch}"
            </div>
          )}
        </div>

        <div className="bg-white">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-gray-300">
              <Loader2 size={48} className="animate-spin text-indigo-500" />
              <span className="font-black text-sm tracking-[0.2em] uppercase">Generating Report...</span>
            </div>
          ) : data.length > 0 ? (
            <div className="p-1">
              <ReusableTable columns={columns} data={globalSearch ? data.filter(r => Object.values(r).some(v => v?.toString().toLowerCase().includes(globalSearch.toLowerCase()))) : data} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-32 text-gray-400 gap-3">
              <Info className="w-12 h-12 text-gray-200" />
              <div className="text-center">
                <p className="font-bold text-sm italic">No transaction records match your filters.</p>
                <p className="text-[10px] uppercase tracking-widest mt-1">Try adjusting the date range or search terms</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;
